import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import AllChannelsSelector, {
  expandChannelSelection,
  selectionTargetsOtas,
} from '../common/AllChannelsSelector';
import { setRateStopSell } from '../../services/api';
import useChannelMappingRows from '../../hooks/useChannelMappingRows';
import { compareRatePlans } from '../../utils/ratePlanSort';
import {
  inventoryDrawerFooterSx,
  inventoryDrawerHeaderSx,
} from '../../utils/inventoryMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import RateActionSuccessDialog, { RATE_SUCCESS_COPY } from './RateActionSuccessDialog';

const THEME_COLOR = '#4ce1b6';

const DAY_OPTIONS = [
  { value: 0, label: 'Sun', shortLabel: 'S', color: '#FF6B6B' },
  { value: 1, label: 'Mon', shortLabel: 'M', color: '#4ECDC4' },
  { value: 2, label: 'Tue', shortLabel: 'T', color: '#45B7D1' },
  { value: 3, label: 'Wed', shortLabel: 'W', color: '#FFA07A' },
  { value: 4, label: 'Thu', shortLabel: 'T', color: '#98D8C8' },
  { value: 5, label: 'Fri', shortLabel: 'F', color: '#F7DC6F' },
  { value: 6, label: 'Sat', shortLabel: 'S', color: '#BB8FCE' },
];

const rowKey = (roomTypeId, ratePlanId) => `${String(roomTypeId)}:${String(ratePlanId)}`;

const OCCUPANCY_GUESTS = {
  s: 1, d: 2, t: 3, q: 4, p: 5, h: 6, hp: 7, o: 8,
  n: 9, de: 10, el: 11, tw: 12, th: 13, fo: 14,
};

const occupancyGuests = (plan) => {
  const code = String(plan?.code || '').trim().toLowerCase();
  const parts = code.split('-').filter(Boolean);
  if (parts.length >= 2 && OCCUPANCY_GUESTS[parts[parts.length - 2]] != null) {
    return OCCUPANCY_GUESTS[parts[parts.length - 2]];
  }
  const name = String(plan?.name || '').toLowerCase();
  const words = [
    ['hept', 7],
    ['hex', 6],
    ['oct', 8],
    ['pent', 5],
    ['quad', 4],
    ['triple', 3],
    ['single', 1],
    ['double', 2],
  ];
  for (const [word, guests] of words) {
    if (name.includes(word)) return guests;
  }
  return null;
};

const planAppliesToRoom = (plan, room) => {
  const appliesAll = plan?.applies_to_all_room_types !== false;
  const ids = Array.isArray(plan?.room_type_ids) ? plan.room_type_ids : [];
  if (!appliesAll && ids.length > 0) {
    if (!ids.some((id) => Number(id) === Number(room.id))) return false;
  }
  const cap = Number(room.max_guests || room.capacity || 0);
  const guests = occupancyGuests(plan);
  if (guests != null && cap > 0 && guests > cap) return false;
  return true;
};

const resolveInitialChannels = () => ['all'];

const resolveInitialRatePlanFilter = ({ platform, context, planRows }) => {
  if (context?.channelId === 'pms') return 'pms';
  if (context?.channelId && context.channelId !== 'all' && context.channelId !== 'pms') {
    return 'channel_manager';
  }
  const contextPlan = planRows.find(
    (row) =>
      String(row.roomTypeId) === String(context?.roomTypeId) &&
      String(row.ratePlanId) === String(context?.ratePlanId)
  );
  if (contextPlan?.isPmsEnabled && !contextPlan?.isChannelManagerEnabled) {
    return 'pms';
  }
  if (contextPlan?.isChannelManagerEnabled) {
    return 'channel_manager';
  }
  if (platform === 'pms') return 'pms';
  return 'channel_manager';
};

/**
 * Stop Sell drawer — rate-plan stopSell via Aiosell rate-restrictions-push.
 *
 * initialContext (optional): prefill when opened from a grid cell
 *   { date, roomTypeId, ratePlanId, action?: 'apply'|'clear' }
 */
export default function RateStopSellDrawer({
  open,
  onClose,
  roomTypes = [],
  ratePlans = [],
  channels = [],
  isDark,
  themeColors,
  onSaved,
  initialContext = null,
  platform = 'channel_manager',
}) {
  const [dateRange, setDateRange] = useState([null, null]);
  const [daySelectionType, setDaySelectionType] = useState('custom');
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [action, setAction] = useState('apply'); // apply (Yes) | clear (No)
  const [ratePlanFilter, setRatePlanFilter] = useState(() =>
    platform === 'pms' ? 'pms' : 'channel_manager'
  );
  const [selectedChannels, setSelectedChannels] = useState(['all']);
  const { mappingPairs, mappingsLoading } = useChannelMappingRows(open);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successDialog, setSuccessDialog] = useState(null);
  const drawerRootRef = useRef(null);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );

  const planRows = useMemo(() => {
    const rooms = Array.isArray(roomTypes) ? roomTypes : [];
    const plans = Array.isArray(ratePlans) ? ratePlans : [];
    const rows = [];
    rooms.forEach((room) => {
      plans.forEach((plan) => {
        if (!planAppliesToRoom(plan, room)) return;
        const isOccupancy = occupancyGuests(plan) != null;
        const isPmsEnabled = !isOccupancy && (
          plan.is_pms_enabled === true || plan.enablePMS === true
          || (
            plan.is_pms_enabled !== false
            && plan.enablePMS !== false
            && plan.is_channel_manager_enabled !== true
            && plan.enableChannelManager !== true
          )
        );
        if (!isPmsEnabled) return;
        rows.push({
          key: rowKey(room.id, plan.id),
          roomTypeId: room.id,
          roomTypeName: room.name || `Room ${room.id}`,
          ratePlanId: plan.id,
          ratePlanName: plan.name || `Plan ${plan.id}`,
          mealPlan: plan.meal_plan || '',
          isPmsEnabled: true,
          isChannelManagerEnabled: false,
        });
      });
    });
    const mappedRows = mappingPairs.map((pair) => ({
      ...pair,
      key: rowKey(pair.roomTypeId, pair.ratePlanId),
      isPmsEnabled: false,
      isChannelManagerEnabled: true,
    }));
    return [...rows, ...mappedRows];
  }, [roomTypes, ratePlans, mappingPairs]);

  const filteredPlanRows = useMemo(
    () =>
      planRows.filter(
        (row) =>
          (ratePlanFilter === 'pms' && row.isPmsEnabled) ||
          (ratePlanFilter === 'channel_manager' && row.isChannelManagerEnabled)
      ),
    [planRows, ratePlanFilter]
  );

  const rowsByRoom = useMemo(() => {
    const map = new Map();
    filteredPlanRows.forEach((row) => {
      if (!map.has(row.roomTypeId)) {
        map.set(row.roomTypeId, { name: row.roomTypeName, rows: [] });
      }
      map.get(row.roomTypeId).rows.push(row);
    });
    map.forEach((group) => {
      group.rows.sort(compareRatePlans);
    });
    return map;
  }, [filteredPlanRows]);

  const resetForm = useCallback(() => {
    const ctx = initialContext;
    if (ctx?.date) {
      const day = moment(ctx.date).startOf('day');
      setDateRange([day.clone(), day.clone().add(1, 'day')]);
      setSelectedDays([day.day()]);
      setDaySelectionType('custom');
    } else {
      setDateRange([moment().add(1, 'day'), moment().add(2, 'day')]);
      setDaySelectionType('custom');
      setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    }

    setRatePlanFilter(resolveInitialRatePlanFilter({ platform, context: ctx, planRows }));
    setSelectedChannels(resolveInitialChannels());
    setAction(ctx?.action === 'clear' || ctx?.action === 'apply' ? ctx.action : 'apply');
    setErrorMessage(null);
    setSuccessDialog(null);

    if (ctx?.roomTypeId != null && ctx?.ratePlanId != null) {
      setSelectedKeys(new Set([rowKey(ctx.roomTypeId, ctx.ratePlanId)]));
    } else {
      const filter = resolveInitialRatePlanFilter({ platform, context: ctx, planRows });
      setSelectedKeys(
        new Set(
          planRows
            .filter((row) =>
              filter === 'pms' ? row.isPmsEnabled : row.isChannelManagerEnabled
            )
            .map((row) => row.key)
        )
      );
    }
  }, [planRows, initialContext, platform, channels]);

  useEffect(() => {
    if (open) resetForm();
    // Reset only when the drawer opens, not when mappings refresh after save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || filteredPlanRows.length === 0) return;
    if (initialContext?.roomTypeId != null && initialContext?.ratePlanId != null) {
      const key = rowKey(initialContext.roomTypeId, initialContext.ratePlanId);
      if (filteredPlanRows.some((row) => row.key === key)) {
        setSelectedKeys(new Set([key]));
        return;
      }
    }
    setSelectedKeys(new Set(filteredPlanRows.map((row) => row.key)));
  }, [open, filteredPlanRows, initialContext]);

  const selectedVisibleCount = filteredPlanRows.filter((row) => selectedKeys.has(row.key)).length;
  const allSelected = filteredPlanRows.length > 0 && selectedVisibleCount === filteredPlanRows.length;
  const someSelected = selectedVisibleCount > 0 && selectedVisibleCount < filteredPlanRows.length;
  const applyStop = action === 'apply';

  const selectDayType = (type) => {
    setDaySelectionType(type);
    if (type === 'weekdays') setSelectedDays([1, 2, 3, 4, 5]);
    else if (type === 'weekend') setSelectedDays([0, 6]);
    else setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        const next = prev.filter((d) => d !== day);
        return next.length ? next : prev;
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const collectDates = () => {
    if (!dateRange[0] || !dateRange[1]) return [];
    const dates = [];
    let current = moment(dateRange[0]).startOf('day');
    const end = moment(dateRange[1]).startOf('day');
    // To is checkout (exclusive), same as stay dates: 24→25 applies only the 24th.
    // Same-day From/To (opened from a grid cell) still applies that one night.
    const lastNight = end.isSame(current, 'day') ? end : end.clone().subtract(1, 'day');
    while (current.isSameOrBefore(lastNight, 'day')) {
      if (selectedDays.includes(current.day())) dates.push(current.format('YYYY-MM-DD'));
      current.add(1, 'day');
    }
    return dates;
  };

  const chipSx = (filled) => ({
    cursor: 'pointer',
    fontWeight: filled ? 700 : 500,
    backgroundColor: filled ? THEME_COLOR : 'transparent',
    color: filled ? '#000' : 'inherit',
    borderColor: filled ? THEME_COLOR : alpha(THEME_COLOR, 0.45),
    borderWidth: filled ? 1.5 : 1,
    '&:hover': {
      backgroundColor: filled ? THEME_COLOR : alpha(THEME_COLOR, 0.08),
      borderColor: THEME_COLOR,
    },
  });

  const handleSave = async () => {
    setErrorMessage(null);
    setSuccessDialog(null);

    if (!dateRange[0] || !dateRange[1]) {
      setErrorMessage('Select a date range');
      return;
    }
    if (dateRange[0].isBefore(moment().startOf('day'), 'day')) {
      setErrorMessage('Cannot set stop sell for past dates');
      return;
    }
    if (selectedVisibleCount === 0) {
      setErrorMessage('Select at least one rate plan');
      return;
    }
    if (ratePlanFilter === 'channel_manager' && !selectionTargetsOtas(selectedChannels)) {
      setErrorMessage('Select at least one OTA channel');
      return;
    }
    const dates = collectDates();
    if (!dates.length) {
      setErrorMessage('No dates match the selected weekdays');
      return;
    }

    const selectedRows = filteredPlanRows.filter((r) => selectedKeys.has(r.key));
    const items = selectedRows.map((r) => ({
      room_type_id: r.roomTypeId,
      rate_plan_id: r.ratePlanId,
    }));
    if (!items.length) {
      setErrorMessage('Select at least one rate plan');
      return;
    }

    setIsSaving(true);
    try {
      const platform = ratePlanFilter === 'pms' ? 'pms' : 'channel_manager';
      const channelIds =
        platform === 'channel_manager'
          ? expandChannelSelection(selectedChannels, channels).filter(
              (id) => id !== 'pms' && id !== 'all'
            )
          : null;
      const results = [
        await setRateStopSell({
          startDate: dates[0],
          endDate: dates[dates.length - 1],
          dates,
          stopSell: applyStop,
          items,
          platform,
          channelIds,
        }),
      ];
      const failed = results.find((result) => !result?.success);
      if (failed) {
        setErrorMessage(failed?.error || failed?.message || 'Stop sell push failed');
        return;
      }
      if (typeof onSaved === 'function') await onSaved();
      const isPms = platform === 'pms';
      setSuccessDialog(
        applyStop
          ? isPms
            ? RATE_SUCCESS_COPY.stopSellApplyPms
            : RATE_SUCCESS_COPY.stopSellApplyChannel
          : isPms
            ? RATE_SUCCESS_COPY.stopSellClearPms
            : RATE_SUCCESS_COPY.stopSellClearChannel
      );
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Stop sell push failed'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !isSaving && onClose()}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 640 },
          overflow: 'hidden',
          backgroundColor: isDark ? themeColors.surface : '#ffffff',
        },
      }}
    >
      <Box
        ref={drawerRootRef}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...inventoryDrawerHeaderSx(isDark),
            position: 'relative',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, lineHeight: 1.2 }}>
              Stop Sell
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rate-plan stop sell · one push
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSaving} sx={{ color: themeColors.subtleText }}>
            <CloseIcon />
          </IconButton>
          {isSaving && (
            <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }} />
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                  Stop sell
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {[
                    { value: 'apply', label: 'Apply (Yes)' },
                    { value: 'clear', label: 'Clear (No)' },
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      onClick={() => setAction(opt.value)}
                      variant="outlined"
                      sx={chipSx(action === opt.value)}
                    />
                  ))}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {applyStop
                  ? 'Blocks bookings on selected rate plans. To is checkout and is not included.'
                  : 'Removes stop sell so selected rate plans can sell again. To is checkout and is not included.'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Date range
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <DatePicker
                  label="From"
                  value={dateRange[0]}
                  minDate={moment().startOf('day')}
                  onChange={(v) => {
                    if (!v) return;
                    const from = v.isBefore(moment().startOf('day'), 'day')
                      ? moment().startOf('day')
                      : v;
                    setDateRange([from, from.clone().add(1, 'day')]);
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateRange[1]}
                  minDate={dateRange[0]?.clone().add(1, 'day')}
                  onChange={(v) => {
                    if (v && dateRange[0] && v.isAfter(dateRange[0])) {
                      setDateRange([dateRange[0], v]);
                    }
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Day Selection
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pl: 1 }}>
                {[
                  { value: 'custom', label: 'Custom' },
                  { value: 'weekdays', label: 'Weekdays' },
                  { value: 'weekend', label: 'Weekend' },
                ].map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Radio
                        size="small"
                        checked={daySelectionType === opt.value}
                        onChange={() => selectDayType(opt.value)}
                        sx={{ color: alpha(THEME_COLOR, 0.6), '&.Mui-checked': { color: THEME_COLOR } }}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </Box>
              {daySelectionType === 'custom' && (
                <Box sx={{ mt: 1.5, pl: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {DAY_OPTIONS.map((day) => {
                    const on = selectedDays.includes(day.value);
                    return (
                      <Chip
                        key={day.value}
                        label={`${day.shortLabel} ${day.label}`}
                        onClick={() => toggleDay(day.value)}
                        variant={on ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: on ? day.color : 'transparent',
                          color: on ? '#fff' : themeColors.subtleText,
                          borderColor: on ? day.color : themeColors.surfaceBorder,
                          fontWeight: on ? 600 : 400,
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>

            {ratePlanFilter === 'channel_manager' ? (
              <Box sx={{ mb: 2.5 }}>
                <AllChannelsSelector
                  channels={channels}
                  value={selectedChannels}
                  onChange={setSelectedChannels}
                  allowPms={false}
                  otaMode="selectable"
                  themeColor={THEME_COLOR}
                  disabled={isSaving}
                  helperText="Choose which OTAs receive this stop sell. All Channels sends to every enabled OTA."
                />
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                PMS stop sell is local only. Switch to Channel to choose OTAs.
              </Typography>
            )}

            <Box
              sx={{
                mb: 2.5,
                borderRadius: 1.5,
                border: `1px solid ${themeColors.surfaceBorder}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr auto',
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  alignItems: 'center',
                  backgroundColor: isDark ? alpha(THEME_COLOR, 0.12) : alpha(THEME_COLOR, 0.1),
                  borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() =>
                    setSelectedKeys(
                      allSelected ? new Set() : new Set(filteredPlanRows.map((r) => r.key))
                    )
                  }
                  sx={{
                    p: 0.5,
                    color: alpha(THEME_COLOR, 0.6),
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: THEME_COLOR },
                  }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  RATEPLANS ({selectedVisibleCount}/{filteredPlanRows.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {[
                    { value: 'pms', label: 'PMS' },
                    { value: 'channel_manager', label: 'Channel' },
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      size="small"
                      label={opt.label}
                      onClick={() => {
                        setRatePlanFilter(opt.value);
                      }}
                      variant="outlined"
                      sx={{
                        ...chipSx(ratePlanFilter === opt.value),
                        height: 24,
                        fontSize: '0.7rem',
                      }}
                    />
                  ))}
                </Box>
              </Box>
              {filteredPlanRows.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {ratePlanFilter === 'channel_manager'
                      ? mappingsLoading
                        ? 'Loading mapped Channel Manager rate plans…'
                        : 'No mapped Channel Manager room types and rate plans. Save channel mappings first.'
                      : 'No PMS meal plans available.'}
                  </Typography>
                </Box>
              ) : (
              Array.from(rowsByRoom.entries()).map(([roomId, group]) => (
                <Box key={roomId}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                      borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: THEME_COLOR, textTransform: 'uppercase' }}
                    >
                      {group.name}
                    </Typography>
                  </Box>
                  {group.rows.map((row) => {
                    const checked = selectedKeys.has(row.key);
                    return (
                      <Box
                        key={row.key}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr',
                          gap: 1,
                          px: 1.25,
                          py: 0.65,
                          alignItems: 'center',
                          borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                          opacity: checked ? 1 : 0.55,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => {
                            setSelectedKeys((prev) => {
                              const next = new Set(prev);
                              if (next.has(row.key)) next.delete(row.key);
                              else next.add(row.key);
                              return next;
                            });
                          }}
                          sx={{
                            p: 0.5,
                            color: alpha(THEME_COLOR, 0.6),
                            '&.Mui-checked': { color: THEME_COLOR },
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {row.ratePlanName}
                          {row.mealPlan ? (
                            <Box component="span" sx={{ color: 'text.secondary' }}>
                              {' '}
                              ({row.mealPlan})
                            </Box>
                          ) : null}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))
              )}
            </Box>

          </LocalizationProvider>
        </Box>

        <Box sx={{ p: 1.5, ...inventoryDrawerFooterSx(isDark), display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" fullWidth onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={isSaving || filteredPlanRows.length === 0}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': { backgroundColor: THEME_COLOR, opacity: 0.9 },
            }}
          >
            {isSaving ? 'Saving…' : applyStop ? 'Apply Stop Sell' : 'Clear Stop Sell'}
          </Button>
        </Box>
        <RateActionSuccessDialog
          open={Boolean(successDialog)}
          title={successDialog?.title}
          message={successDialog?.message}
          container={() => drawerRootRef.current || undefined}
          onClose={() => {
            setSuccessDialog(null);
            onClose();
          }}
        />
      </Box>
    </Drawer>
  );
}
